import { useEffect, useMemo, useRef, useState } from "react";
import { DeleteOutlined } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Dropzone from "react-dropzone";
import FlexBetween from "components/FlexBetween";
import { resolveAssetUrl } from "utils/media";
import { useSelector } from "react-redux";
import { SCHOOLS } from "../../constants/schools";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const MAX_POST_IMAGES = 2;

const PostEditorDialog = ({
  open,
  onClose,
  token,
  postId,
  initialCaption = "",
  initialSchool = "",
  initialAuthorSchool = "",
  initialImageUrls = [],
  onSaved,
}) => {
  const currentUser = useSelector((state) => state.user);
  const isAdmin = Boolean(currentUser?.isAdmin);
  const [caption, setCaption] = useState(initialCaption);
  const [school, setSchool] = useState(initialSchool);
  const [existingImages, setExistingImages] = useState(initialImageUrls);
  const [newImages, setNewImages] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const initialCaptionRef = useRef(initialCaption);
  const initialSchoolRef = useRef(initialSchool);
  const initialAuthorSchoolRef = useRef(initialAuthorSchool);
  const initialImageUrlsRef = useRef(initialImageUrls);

  useEffect(() => {
    initialCaptionRef.current = initialCaption;
  }, [initialCaption]);

  useEffect(() => {
    initialSchoolRef.current = initialSchool;
  }, [initialSchool]);

  useEffect(() => {
    initialAuthorSchoolRef.current = initialAuthorSchool;
  }, [initialAuthorSchool]);

  useEffect(() => {
    initialImageUrlsRef.current = initialImageUrls;
  }, [initialImageUrls]);

  useEffect(() => {
    if (!open) return;
    setCaption(initialCaptionRef.current);
    setSchool(initialSchoolRef.current || "");
    setExistingImages([...initialImageUrlsRef.current]);
    setNewImages([]);
    setIsSaving(false);
  }, [open, postId]);

  const isSchoolOverrideActive =
    String(school || "").trim() !== String(initialAuthorSchoolRef.current || "").trim();

  const totalImages = existingImages.length + newImages.length;

  const previewImages = useMemo(
    () => [
      ...existingImages.map((url, index) => ({
        id: `existing-${index}-${url}`,
        name: `Bild ${index + 1}`,
        src: resolveAssetUrl(url),
        type: "existing",
      })),
      ...newImages.map((file, index) => ({
        id: `new-${index}-${file.name}`,
        name: file.name,
        src: URL.createObjectURL(file),
        type: "new",
      })),
    ],
    [existingImages, newImages]
  );

  useEffect(() => {
    return () => {
      previewImages.forEach((image) => {
        if (image.type === "new") {
          URL.revokeObjectURL(image.src);
        }
      });
    };
  }, [previewImages]);

  const handleDrop = (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    setNewImages((prev) => {
      const remainingSlots = Math.max(
        0,
        MAX_POST_IMAGES - existingImages.length - prev.length
      );
      return [...prev, ...acceptedFiles.slice(0, remainingSlots)];
    });
  };

  const handleSave = async () => {
    if (!token || !postId || totalImages < 1 || isSaving) return;

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("caption", caption.trim());
      formData.append("retainedImageUrls", JSON.stringify(existingImages));
      if (isAdmin) {
        formData.append("school", String(school || "").trim());
      }
      newImages.forEach((image) => formData.append("images", image));

      const response = await fetch(`${BASE_URL}/posts/${postId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Beitrag konnte nicht gespeichert werden.");
      }

      if (onSaved) onSaved(data);
      onClose();
    } catch (error) {
      setSnackbarState({
        open: true,
        message: error.message || "Beitrag konnte nicht gespeichert werden.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Beitrag bearbeiten</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Beschreibung"
            fullWidth
            multiline
            minRows={2}
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
          />
          {isAdmin && (
            <>
              <Autocomplete
                freeSolo
                options={SCHOOLS}
                value={school}
                onChange={(_, value) => setSchool(String(value || ""))}
                onInputChange={(_, value) => setSchool(String(value || ""))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Schule (Admin-Override)"
                    helperText="Du kannst eine Schule aus der Liste waehlen oder eigenen Text eingeben."
                    sx={{ mt: 1.5 }}
                  />
                )}
              />
              {isSchoolOverrideActive && (
                <Typography color="warning.main" fontSize="0.8rem" mt={0.6}>
                  Admin-Override aktiv: Angezeigte Schule weicht von der Schule des Autors ab.
                </Typography>
              )}
            </>
          )}
          <Dropzone
            acceptedFiles=".jpg,.jpeg,.png,.webp"
            multiple
            maxFiles={MAX_POST_IMAGES}
            onDrop={handleDrop}
            disabled={totalImages >= MAX_POST_IMAGES}
          >
            {({ getRootProps, getInputProps }) => (
              <Box
                {...getRootProps()}
                border="2px dashed"
                borderColor="primary.main"
                p="1rem"
                mt="1rem"
                sx={{
                  cursor: totalImages >= MAX_POST_IMAGES ? "default" : "pointer",
                  opacity: totalImages >= MAX_POST_IMAGES ? 0.6 : 1,
                }}
              >
                <input {...getInputProps()} />
                <Typography>
                  {totalImages >= MAX_POST_IMAGES
                    ? "Maximal 2 Bilder erreicht"
                    : "Bilder hier ablegen oder klicken (max. 2)"}
                </Typography>
              </Box>
            )}
          </Dropzone>
          <Typography mt="0.5rem" color="text.secondary">
            {totalImages}/2 Bilder ausgewählt
          </Typography>

          {previewImages.length > 0 && (
            <Box
              display="grid"
              gap="0.75rem"
              mt="1rem"
              sx={{ gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, 1fr)" } }}
            >
              {previewImages.map((image, index) => (
                <Box key={image.id} sx={{ minWidth: 0 }}>
                  <Box
                    component="img"
                    src={image.src}
                    alt={image.name}
                    sx={{
                      width: "100%",
                      height: 140,
                      objectFit: "cover",
                      borderRadius: "8px",
                      display: "block",
                    }}
                  />
                  <FlexBetween mt="0.5rem" gap="0.5rem" sx={{ minWidth: 0 }}>
                    <Typography
                      fontSize="0.75rem"
                      noWrap
                      sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {image.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (image.type === "existing") {
                          setExistingImages((prev) =>
                            prev.filter((_, existingIndex) => existingIndex !== index)
                          );
                          return;
                        }
                        const newImageIndex = index - existingImages.length;
                        setNewImages((prev) =>
                          prev.filter((_, fileIndex) => fileIndex !== newImageIndex)
                        );
                      }}
                    >
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </FlexBetween>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || totalImages < 1}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbarState((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbarState((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity={snackbarState.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PostEditorDialog;
